import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';

import { SUPPORTED_LANGUAGES, isRtlLanguage } from './languages';
import { loadLocaleInto } from './locale-loader';

export { SUPPORTED_LANGUAGES, RTL_LANGUAGES, type SupportedLanguage } from './languages';

const resources = { en: { translation: en } };

/** Fetch a language's chunk into this instance. See ./locale-loader. */
export function ensureLanguage(language: string): Promise<void> {
  return loadLocaleInto(i18n, language).then(() => undefined);
}

/**
 * Keep <html lang> and <html dir> in step with the UI language. `lang` is what
 * shared/lib/intl reads to pick date, time and number formats, and `dir` is
 * what flips the layout for Arabic and Urdu — so both must be right from the
 * first paint, not only after someone uses the language switcher.
 */
function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return;
  const base = (language || 'en').split('-')[0] ?? 'en';
  document.documentElement.lang = base;
  document.documentElement.dir = isRtlLanguage(base) ? 'rtl' : 'ltr';
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // A browser set to de-AT or pt-BR should still get de / pt.
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      // Locales arrive after init now, so components have to re-render when a
      // bundle is added, not only when the language changes.
      bindI18nStore: 'added',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'unipostudio-language',
    },
  });

// Only English is bundled, so the detected language still has to be fetched.
// The document language is set immediately either way, so layout direction is
// right from the first paint even while the strings are still arriving.
syncDocumentLanguage(i18n.language ?? 'en');
if (typeof i18n.on === 'function') i18n.on('languageChanged', syncDocumentLanguage);
void ensureLanguage(i18n.language ?? 'en');

export default i18n;
